import { useSyncExternalStore } from "react";

/**
 * Круг групп окон TigerTrade (§10 1.45, design.md D3). Хранится последняя
 * отправленная группа, а не индекс: если набор поправили, круг не ломается,
 * а начинается с первой группы. Значение лежит в `localStorage` отдельным
 * ключом, общим для вкладок, и читается в момент клика. Доступ к хранилищу —
 * функцией и в `try/catch` без журнала, как у `terminal-settings.ts`; при
 * недоступном хранилище круг живёт в памяти до перезагрузки.
 */

export const TERMINAL_LAST_GROUP_STORAGE_KEY = "screener.terminal.tigertrade.last-group";
/** Круг связок MetaScalp (§89 п. 6) — тот же механизм, свой ключ. */
export const TERMINAL_LAST_BINDING_STORAGE_KEY = "screener.terminal.metascalp.last-binding";

/** Группа, взятая под отправку, и та, что была до неё (для отката при `not_sent`). */
export interface GroupReservation {
  group: string;
  previous: string | null;
}

/**
 * Следующая группа круга: после `last`, а после последней — первая. Пустой
 * набор — `null`; `last` нет в наборе или не задан — первая группа.
 */
export function nextGroup(groups: readonly string[], last: string | null): string | null {
  const first = groups[0];
  if (first === undefined) {
    return null;
  }
  if (last === null) {
    return first;
  }
  const index = groups.indexOf(last);
  if (index === -1) {
    return first;
  }
  return groups[(index + 1) % groups.length] ?? first;
}

export interface GroupRotation {
  /** Последняя отправленная группа; `null` — не было или запись пуста. */
  last: () => string | null;
  /**
   * Берёт следующую группу и сразу записывает её как последнюю: отправки
   * подряд до ответа терминала получают разные группы. Пустой набор — `null`.
   */
  reserve: (groups: readonly string[]) => GroupReservation | null;
  /**
   * Возвращает прежнюю последнюю группу, но только если после резерва круг
   * никто не сдвинул (ни другая отправка, ни другая вкладка).
   */
  release: (reservation: GroupReservation) => void;
  subscribe: (listener: () => void) => () => void;
}

export function createGroupRotation(
  getStorage: () => Storage | null,
  storageKey: string = TERMINAL_LAST_GROUP_STORAGE_KEY,
): GroupRotation {
  const listeners = new Set<() => void>();
  // Запасное значение на случай недоступного хранилища; при рабочем — зеркало записи.
  let memory: string | null = null;

  const storage = (): Storage | null => {
    try {
      return getStorage();
    } catch {
      return null;
    }
  };

  const read = (): string | null => {
    const target = storage();
    if (target === null) {
      return memory;
    }
    try {
      const raw = target.getItem(storageKey);
      return raw === null || raw === "" ? null : raw;
    } catch {
      return memory;
    }
  };

  const write = (value: string | null): void => {
    memory = value;
    const target = storage();
    if (target !== null) {
      try {
        if (value === null) {
          target.removeItem(storageKey);
        } else {
          target.setItem(storageKey, value);
        }
      } catch {
        // Хранилище недоступно — круг живёт в памяти до перезагрузки.
      }
    }
    for (const listener of listeners) {
      listener();
    }
  };

  return {
    last: read,
    reserve(groups) {
      const previous = read();
      const group = nextGroup(groups, previous);
      if (group === null) {
        return null;
      }
      write(group);
      return { group, previous };
    },
    release(reservation) {
      if (read() === reservation.group) {
        write(reservation.previous);
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const groupRotation = createGroupRotation(() => window.localStorage);
export const bindingRotation = createGroupRotation(
  () => window.localStorage,
  TERMINAL_LAST_BINDING_STORAGE_KEY,
);

/** Подписчики хука; одна подписка на `window` на всех, а не по одной на кнопку. */
const hookListeners = new Set<() => void>();

// Другая вкладка сдвинула круг — подпись кнопки здесь тоже обновляется.
function onStorage(event: StorageEvent): void {
  if (
    event.key === TERMINAL_LAST_GROUP_STORAGE_KEY ||
    event.key === TERMINAL_LAST_BINDING_STORAGE_KEY ||
    event.key === null
  ) {
    for (const listener of hookListeners) {
      listener();
    }
  }
}

function subscribeToRotation(rotation: GroupRotation, listener: () => void): () => void {
  const unsubscribe = rotation.subscribe(listener);
  hookListeners.add(listener);
  if (hookListeners.size === 1) {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    unsubscribe();
    hookListeners.delete(listener);
    if (hookListeners.size === 0) {
      window.removeEventListener("storage", onStorage);
    }
  };
}

const subscribeToGroups = (listener: () => void) => subscribeToRotation(groupRotation, listener);
const subscribeToBindings = (listener: () => void) =>
  subscribeToRotation(bindingRotation, listener);

/** Следующая группа круга для подписи кнопки; перерисовывается, когда круг сдвинулся. */
export function useNextGroup(groups: readonly string[]): string | null {
  const last = useSyncExternalStore(subscribeToGroups, groupRotation.last, () => null);
  return nextGroup(groups, last);
}

/** Следующая связка круга MetaScalp для подписи кнопки. */
export function useNextBinding(bindings: readonly string[]): string | null {
  const last = useSyncExternalStore(subscribeToBindings, bindingRotation.last, () => null);
  return nextGroup(bindings, last);
}
